/**
 * The Chats page's view of Pinned chats (ADR 0035): the fans pinned on one
 * Account, out of the person's one board on the server (workspace/pinStore),
 * so a pin made here shows in Workspace's Pinned and on every other device.
 */
import type { PinResult, PinStore } from '../workspace/pinStore'
import { usersFromListAnswer } from './chatRows'

export interface FanSnapshot {
  name?: string | null
  username?: string | null
  avatar?: string | null
}

export type ToggleResult = PinResult | 'unpinned' | 'unpin-failed'

export interface AccountPins {
  subscribe: (fn: () => void) => () => void
  /** Fan ids pinned on this Account; the same Set until the pins change. */
  get: () => ReadonlySet<string>
  toggle: (fanId: string, fan?: FanSnapshot) => Promise<ToggleResult>
}

/** This Account's slice of the server board. */
export function serverAccountPins(store: PinStore, accountId: string): AccountPins {
  let seen: unknown = null
  let ids: ReadonlySet<string> = new Set()
  return {
    subscribe: store.subscribe,
    get() {
      const { pins } = store.get()
      if (pins !== seen) {
        seen = pins
        ids = new Set(pins.filter((p) => p.account_id === accountId).map((p) => p.fan_id))
      }
      return ids
    },
    async toggle(fanId, fan) {
      if (store.isPinned(accountId, fanId)) return (await store.remove(accountId, fanId)) ? 'unpinned' : 'unpin-failed'
      return store.add({
        account_id: accountId,
        fan_id: fanId,
        fan_name: fan?.name || null,
        fan_username: fan?.username || null,
        fan_avatar: fan?.avatar || null,
      })
    },
  }
}

/** Pins that live only in memory (tests, and pages without a signed-in CRM person). */
export function memoryPins(start: Iterable<string> = []): AccountPins {
  let ids: ReadonlySet<string> = new Set(start)
  const listeners = new Set<() => void>()
  return {
    subscribe(fn) {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    get: () => ids,
    async toggle(fanId) {
      const next = new Set(ids)
      const pinning = !next.has(fanId)
      if (pinning) next.add(fanId)
      else next.delete(fanId)
      ids = next
      listeners.forEach((fn) => fn())
      return pinning ? 'pinned' : 'unpinned'
    },
  }
}

const LEGACY_KEY = (scope: string) => `crm_chats_pinned_chats_${scope}`
const uploaded = new Set<string>()

/** OnlyFans' users/list for the fans (through the gateway); null when it cannot say. */
export type LookupUsers = (fanIds: string[]) => Promise<unknown>

/**
 * Pins this browser kept for the Account before pins moved to the server go up
 * once, named from OnlyFans (the browser kept only fan ids) so Workspace's
 * Pinned can show who they are; the browser copy is dropped once the server
 * took them.
 */
export async function uploadBrowserPins(store: PinStore, accountId: string, scope: string, lookup?: LookupUsers): Promise<void> {
  if (uploaded.has(scope)) return
  uploaded.add(scope)
  let fanIds: string[] = []
  try {
    const raw = JSON.parse(localStorage.getItem(LEGACY_KEY(scope)) || '[]')
    fanIds = Array.isArray(raw) ? raw.map(String).filter(Boolean) : []
  } catch {
    return
  }
  if (!fanIds.length) return
  const users = new Map<string, Record<string, unknown>>()
  try {
    if (lookup) for (const u of usersFromListAnswer(await lookup(fanIds))) users.set(String(u.id), u)
  } catch {
    /* unnamed is fine: the chat state names them once the fan writes */
  }
  const text = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null)
  const res = await store.upload(
    fanIds.map((fan_id) => {
      const u = users.get(fan_id)
      return { account_id: accountId, fan_id, fan_name: text(u?.name), fan_username: text(u?.username), fan_avatar: text(u?.avatar) }
    }),
  )
  if (!res.ok) {
    uploaded.delete(scope)
    return
  }
  try {
    localStorage.removeItem(LEGACY_KEY(scope))
  } catch {
    /* storage unavailable */
  }
}
